import { useEffect, useState } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Super-admin Refund Queue — settlement-gated partial refunds. Each suspended
 * refund lists the provider items InteGolf must clear: cancel each hotel room
 * (auto, via Expedia) and each golf spot (manual phone call), then record the
 * money actually recovered per item. Once every item is recovered the request
 * flips to "ready" and the customer refund releases automatically.
 *
 * Reseller-owned items (Mode B golf) are shown read-only — the reseller clears
 * those from their own portal.
 */
const money = (n, cur = 'CAD') => (n == null ? '—' : `${cur} ${Number(n).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)
const fmtDate = v => { if (!v) return '—'; const d = new Date(String(v).slice(0, 10) + 'T00:00:00'); return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }

const REQ_BADGE = {
  pending:     ['#fef3c7', '#92400e'], in_progress: ['#dbeafe', '#1e40af'],
  ready:       ['#dcfce7', '#166534'], completed:   ['#e2e8f0', '#475569'], aborted: ['#fee2e2', '#991b1b'],
}
const ITEM_BADGE = {
  pending:   ['#fef3c7', '#92400e'], cancelled: ['#e0e7ff', '#3730a3'],
  recovered: ['#dcfce7', '#166534'], skipped:   ['#e2e8f0', '#475569'],
}

export default function RefundQueue() {
  const [requests, setRequests] = useState(null)
  const [history, setHistory] = useState(null)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(null) // item id being acted on

  function load() {
    tournamentApi.get('/admin/resellers/refund-queue')
      .then(r => setRequests(r.data.requests || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
    tournamentApi.get('/admin/resellers/refund-queue/history')
      .then(r => setHistory(r.data.history || []))
      .catch(() => {})
    // Nudge the nav badge (in AdminShell) to refetch its count.
    window.dispatchEvent(new Event('integolf:refunds-updated'))
  }
  useEffect(load, [])

  async function cancelHotel(item) {
    if (!window.confirm(`Cancel ${item.qty} room(s) — "${item.label}" — with Expedia? This releases the room(s).`)) return
    setBusy(item.id); setErr(null)
    try {
      const { data } = await tournamentApi.post(`/admin/resellers/refund-items/${item.id}/cancel-hotel`, {})
      window.alert(data.note || 'Room(s) cancelled.')
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }

  async function recordRecovery(item, form) {
    setBusy(item.id); setErr(null)
    try {
      const { data } = await tournamentApi.patch(`/admin/resellers/refund-items/${item.id}`, {
        actualRecovery: form.actualRecovery === '' ? null : Number(form.actualRecovery),
        providerRef: form.providerRef || null,
        refundDate: form.refundDate || null,
        note: form.note || null,
        status: 'recovered',
      })
      if (data.released) window.alert('That was the last item — the customer refund has been released.')
      else if (data.releaseError) window.alert(`Recorded, but the customer refund could not be released automatically:\n${data.releaseError}\n\nUse “Release refund now” to retry.`)
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }

  async function releaseNow(r) {
    if (!window.confirm(`Release the ${money(r.customerRefundAmount, r.currency)} refund to ${r.customerName || 'the customer'} now?`)) return
    setBusy('req-' + r.id); setErr(null)
    try {
      await tournamentApi.post(`/admin/resellers/refund-requests/${r.id}/release`, {})
      window.alert('Refund released to the customer.')
      load()
    } catch (e) { setErr(e?.response?.data?.message || e.message) }
    finally { setBusy(null) }
  }

  return (
    <AdminShell title="Refund Queue">
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22 }}>Refund Queue</h1>
        <p style={{ color: '#64748b', fontSize: 13, margin: 0, maxWidth: 720, lineHeight: 1.5 }}>
          Suspended customer refunds waiting on provider cancellations. Cancel each hotel room (auto) and golf spot (call the course),
          then record the money recovered. When every item is recovered, the customer refund releases automatically.
        </p>
      </div>

      {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>{err}</div>}

      {requests === null ? <div style={{ color: '#64748b' }}>Loading…</div>
        : requests.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 12, background: '#f8fafc', color: '#64748b' }}>
            No refunds waiting. When a reseller issues a partial refund, it lands here for you to clear with the providers.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 16 }}>
            {requests.map(r => (
              <RequestCard key={r.id} r={r} busy={busy} onCancelHotel={cancelHotel} onRecord={recordRecovery} onRelease={releaseNow} />
            ))}
          </div>
        )}

      <HistorySection history={history} />
    </AdminShell>
  )
}

function HistorySection({ history }) {
  if (!history || history.length === 0) return null
  return (
    <div style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 16, margin: '0 0 10px', color: '#334155' }}>Recent refunds</h2>
      <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'hidden' }}>
        {history.map(h => {
          const [b, f] = h.status === 'completed' ? ['#dcfce7', '#166534'] : ['#fee2e2', '#991b1b']
          return (
            <div key={h.id} style={{ padding: '12px 14px', borderTop: '1px solid #f1f5f9', display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', background: '#fff' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                  {h.tripHref ? <a href={h.tripHref} style={{ color: '#0f172a', textDecoration: 'none' }}>{h.tripLabel}</a> : h.tripLabel}
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: b, color: f }}>{h.status === 'completed' ? 'refunded' : 'cancelled'}</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                  {h.resellerName || '—'} · {h.customerName || '—'} · Mode {h.mode} · {fmtDate(h.completedAt)}{h.reason ? ` · ${h.reason}` : ''}
                </div>
                {h.status === 'completed' && h.items.length > 0 && (
                  <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4 }}>
                    {h.items.map((it, i) => (
                      <span key={i}>{i > 0 ? ' · ' : ''}{it.type === 'hotel' ? '🏨' : '⛳'} {it.label}{it.recovered != null ? ` (recovered ${money(it.recovered, h.currency)})` : ''}{it.providerRef ? ` ref ${it.providerRef}` : ''}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ fontWeight: 700, color: h.status === 'completed' ? '#166534' : '#94a3b8', textAlign: 'right' }}>
                {h.status === 'completed' ? money(h.amount, h.currency) : <span style={{ textDecoration: 'line-through' }}>{money(h.amount, h.currency)}</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function RequestCard({ r, busy, onCancelHotel, onRecord, onRelease }) {
  const [rb, rf] = REQ_BADGE[r.status] || ['#e2e8f0', '#475569']
  const trip = r.quoteNumber ? `${r.quoteNumber}${r.tripName ? ` · ${r.tripName}` : ''}` : `Booking #${r.bookingId}${r.tripName ? ` · ${r.tripName}` : ''}`
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{trip}</div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
            {r.resellerName || `Reseller #${r.resellerAccountId}`} · {r.customerName || '—'} · {fmtDate(r.checkInDate)} → {fmtDate(r.checkOutDate)}
          </div>
          {r.reason && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Reason: {r.reason}</div>}
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{money(r.customerRefundAmount, r.currency)}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Mode {r.mode} · to customer</div>
          <span style={{ display: 'inline-block', marginTop: 6, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: rb, color: rf }}>{r.status.replace('_', ' ')}</span>
        </div>
      </div>

      {r.status === 'ready' && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: '#166534' }}>
            {r.items.length ? '✓ All items recovered — ready to release the customer refund.' : 'Ready to release the customer refund (nothing to cancel with providers).'}
          </span>
          <button onClick={() => onRelease(r)} disabled={busy === 'req-' + r.id}
            style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700, border: 'none', background: busy === 'req-' + r.id ? '#93c5a3' : '#166534', color: '#fff', borderRadius: 6, cursor: busy === 'req-' + r.id ? 'default' : 'pointer' }}>
            {busy === 'req-' + r.id ? 'Releasing…' : 'Release refund now'}
          </button>
        </div>
      )}

      <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
        {r.items.map(it => (
          <ItemRow key={it.id} it={it} currency={r.currency} busy={busy === it.id} onCancelHotel={onCancelHotel} onRecord={onRecord} />
        ))}
      </div>
    </div>
  )
}

function ItemRow({ it, currency, busy, onCancelHotel, onRecord }) {
  const [ib, iff] = ITEM_BADGE[it.status] || ['#e2e8f0', '#475569']
  const [form, setForm] = useState({ actualRecovery: it.customerValue ?? '', providerRef: '', refundDate: '', note: '' })
  const isReseller = it.owner === 'reseller'
  const isHotel = it.itemType === 'hotel_room'
  const done = it.status === 'recovered' || it.status === 'skipped'
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const inp = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5, width: '100%', boxSizing: 'border-box' }

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#fbfcfe' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>{isHotel ? '🏨' : '⛳'} {it.label}</span>
          <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: isReseller ? '#7c3aed' : '#0369a1', background: isReseller ? '#f5f3ff' : '#e0f2fe', padding: '1px 7px', borderRadius: 999 }}>
            {isReseller ? 'reseller' : 'InteGolf'}
          </span>
          <span style={{ marginLeft: 8, fontSize: 11.5, color: '#94a3b8' }}>retail {money(it.customerValue, currency)}</span>
        </div>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: ib, color: iff }}>{it.status}</span>
      </div>

      {done && (it.actualRecovery != null || it.providerRef || it.refundDate || it.note) && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#475569' }}>
          Recovered {money(it.actualRecovery, currency)}{it.refundDate ? ` on ${fmtDate(it.refundDate)}` : ''}{it.providerRef ? ` · ref ${it.providerRef}` : ''}{it.note ? ` · ${it.note}` : ''}
        </div>
      )}

      {isReseller && !done && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#7c3aed' }}>The reseller cancels this with the course and records it from their portal.</div>
      )}

      {!isReseller && !done && (
        <div style={{ marginTop: 10 }}>
          {isHotel && it.status === 'pending' && (
            <button onClick={() => onCancelHotel(it)} disabled={busy}
              style={{ marginBottom: 10, padding: '7px 12px', fontSize: 12.5, fontWeight: 600, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#3730a3', borderRadius: 6, cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Cancelling…' : `Cancel ${it.qty} room(s) with Expedia`}
            </button>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Recovered {currency}</label>
              <input type="number" min="0" step="0.01" value={form.actualRecovery} onChange={e => set('actualRecovery', e.target.value)} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>{isHotel ? 'Cancel ref' : 'Refund ref #'}</label>
              <input value={form.providerRef} onChange={e => set('providerRef', e.target.value)} placeholder={isHotel ? 'Expedia ref' : 'course ref'} style={inp} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Refund date</label>
              <input type="date" value={form.refundDate} onChange={e => set('refundDate', e.target.value)} style={inp} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 3 }}>Note (fees, partial refund, etc.)</label>
              <input value={form.note} onChange={e => set('note', e.target.value)} style={inp} />
            </div>
          </div>
          <button onClick={() => onRecord(it, form)} disabled={busy}
            style={{ marginTop: 10, padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', background: busy ? '#93c5a3' : '#166534', color: '#fff', borderRadius: 6, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Saving…' : 'Mark recovered'}
          </button>
        </div>
      )}
    </div>
  )
}
